import Card from "@/design-system/Card/Card";
import { Collapsible } from "@/design-system/Collapsible/Collapsible";
import Input from "@/design-system/Input/Input";
import { ItemScoreDiamond } from "@/design-system/ItemScore/ItemScoreDiamond";
import { CoinIcon } from "@/designSystemIcons";
import { cn } from "@/lib/utils";
import type { GalleryMilestone } from "@repo/shared/api";
import {
  LEVEL_LIMITS,
  type LevelLimitKey,
  compact,
  formatAmountInput,
  grouped,
} from "@repo/shared/gallery";
import { useTranslation } from "@repo/shared/i18n";
import {
  ArrowLeftRight,
  ChevronDown,
  Layers,
  Lock,
  SlidersHorizontal,
} from "lucide-react";
import { ReactNode } from "react";

import { GalleryMilestoneTrack } from "./GalleryMilestoneTrack";

const LIMIT_ICON: Record<LevelLimitKey, ReactNode> = {
  coinBalance: <CoinIcon size={14} />,
  maxTaxLoss: <CoinIcon size={14} />,
  maxTransfers: <ArrowLeftRight className="size-3.5" />,
  maxSets: <Layers className="size-3.5" />,
};
// The score target and limits share the row; Customize takes what it needs at the end.
const LIMITS_GRID =
  "grid grid-cols-2 gap-2.5 mobile:grid-cols-3 large-desktop:grid-cols-[repeat(5,minmax(0,1fr))_auto]";

/** The Levels page's planner, styled like the Gallery Planner: the target level, the limits and Build Plan. */
export const GalleryLevelPlanner = ({
  milestones,
  level,
  onPick,
  limitFields,
  limitPlaceholders,
  onLimitChange,
  customize,
  footer,
  open,
  onToggle,
}: {
  milestones: GalleryMilestone[];
  level?: number;
  onPick: (level: number) => void;
  limitFields: Record<LevelLimitKey, string>;
  limitPlaceholders: Record<LevelLimitKey, string>;
  onLimitChange: (key: LevelLimitKey, value: string) => void;
  /** The Customize button, beside the limits. */
  customize: ReactNode;
  /** Build Plan, its Credit cost and the build's status line. */
  footer: ReactNode;
  open: boolean;
  onToggle: () => void;
}) => {
  const { t } = useTranslation("gallery");
  const targetScore = milestones.find((m) => m.level === level)?.threshold;

  return (
    <Card
      hoverable={false}
      // Phones: edge to edge, top and bottom borders only.
      className="flex flex-col p-4 max-mobile:-mx-4 max-mobile:rounded-none max-mobile:border-x-0"
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="group/planner flex min-w-0 items-center gap-3 text-left"
      >
        <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-primary-action/15 text-blue-200">
          <SlidersHorizontal className="size-5" />
        </span>
        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="text-body-lg text-neutral-100">
            {t("levels.planner.title")}
          </span>
          <span className="text-body text-neutral-400 max-mobile:truncate">
            {t("levels.planner.description")}
          </span>
        </span>
        {/* Folded, it still says which level the plan aims for. */}
        {level !== undefined && (
          <span className="whitespace-nowrap text-sm font-semibold text-blue-200 group-hover/planner:text-blue-100 max-mobile:hidden">
            {targetScore === undefined
              ? t("levels.level", { level })
              : t("levels.planner.picked", {
                  level,
                  score: compact.format(targetScore),
                })}
          </span>
        )}
        <span className="text-blue-200 group-hover/planner:text-blue-100">
          <ChevronDown
            strokeWidth={2.5}
            className={cn(
              "size-4 transition-transform duration-200",
              open && "rotate-180",
            )}
          />
        </span>
      </button>
      {/* Phones: past the card's padding, so its clip doesn't cut the carousel short of the screen edge. */}
      <Collapsible open={open} className="max-mobile:-mx-4">
        <div className="flex flex-col gap-5 pt-4 max-mobile:px-4">
          <GalleryMilestoneTrack
            milestones={milestones}
            level={level}
            onPick={onPick}
          />
          <div className={LIMITS_GRID}>
            {/* Locked to the picked milestone; the small Input's look, without its clear button. */}
            <div className="flex min-w-0 flex-col gap-1.5">
              <span className="truncate text-xs font-semibold uppercase text-neutral-400">
                {t("levels.planner.targetScore")}
              </span>
              <span className="flex h-8 items-center gap-x-2 rounded-[20px] border border-neutral-700 bg-neutral-800/50 px-3 text-base text-neutral-300">
                <ItemScoreDiamond size={14} />
                <span className="flex-1 truncate tabular-nums">
                  {targetScore === undefined
                    ? "–"
                    : grouped.format(targetScore)}
                </span>
                <Lock className="size-3.5 shrink-0 text-neutral-500" />
              </span>
            </div>
            {LEVEL_LIMITS.map(({ key, label }) => (
              <label key={key} className="flex min-w-0 flex-col gap-1.5">
                <span className="truncate text-xs font-semibold uppercase text-neutral-400">
                  {t(`plan.limits.${label}`)}
                </span>
                <Input
                  size="small"
                  inputMode="decimal"
                  icon={LIMIT_ICON[key]}
                  placeholder={limitPlaceholders[key]}
                  value={limitFields[key]}
                  onChange={(v) => onLimitChange(key, formatAmountInput(v))}
                />
              </label>
            ))}
            {/* Beside the limits, on the inputs' baseline. */}
            <div className="flex min-w-0 items-end">{customize}</div>
          </div>
        </div>
      </Collapsible>
      {/* Build Plan and its status, under the inputs it builds from; stacked on phones. Folded, phones
          keep it under the header, as their step stack opens on Sync and hides Start planning. */}
      {(open || level !== undefined) && (
        <div
          className={cn(
            "mt-5 flex flex-col gap-3 border-t border-neutral-700 pt-4 mobile:flex-row mobile:items-center",
            !open && "mt-4 mobile:hidden",
          )}
        >
          {footer}
        </div>
      )}
    </Card>
  );
};
